import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  User, 
  FileText, 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  AlertCircle, 
  PieChart,
  BarChart3,
  Clock,
  CheckCircle,
  Building,
  CreditCard
} from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'
import FinancialDataCharts from '../../components/FinancialDataCharts'

const CustomerDashboard = () => {
  const { customer } = useCustomerAuth()
  const [dashboardData, setDashboardData] = useState(null)
  const [loanData, setLoanData] = useState(null)
  const [financialStatements, setFinancialStatements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchAllDashboardData()
  }, [])

  const fetchAllDashboardData = async () => {
    try {
      setLoading(true)
      setError('')
      
      // Fetch dashboard data, loans, and financial statements in parallel
      const [dashboardResponse, loansResponse, statementsResponse] = await Promise.all([
        apiService.getCustomerDashboardData().catch(err => ({ success: false, message: err.message })),
        apiService.getCustomerLoans().catch(err => ({ success: false, message: err.message })),
        apiService.getCustomerFinancialStatements().catch(err => ({ success: false, message: err.message }))
      ])

      // Process dashboard data
      if (dashboardResponse.success) {
        setDashboardData(dashboardResponse.data)
      }

      // Process loan data
      if (loansResponse.success) {
        setLoanData(loansResponse.data)
      } else {
        console.warn('Failed to fetch loans:', loansResponse.message)
      }

      // Process financial statements
      if (statementsResponse.success) {
        setFinancialStatements(statementsResponse.data || [])
      } else {
        console.warn('Failed to fetch financial statements:', statementsResponse.message)
      }

    } catch (error) {
      setError('Failed to load dashboard data')
      console.error('Dashboard error:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchAllDashboardData}
            className="btn btn-primary mt-4"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 mt-2">
                Member: {customer?.memberNo}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/customer/financial-data"
                className="btn btn-primary"
              >
                <FileText className="h-5 w-5 mr-2" />
                Submit Financial Data
              </Link>
              <Link
                to="/customer/my-statements"
                className="btn btn-secondary"
              >
                <FileText className="h-5 w-5 mr-2" />
                My Statements
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Financial Statements</p>
                <p className="text-2xl font-bold text-gray-900">
                  {financialStatements.length}
                </p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <CreditCard className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Loans</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loanData?.totalApplications || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-warning-100">
                <TrendingUp className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Loans</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loanData?.loanApplications?.filter(loan => 
                    ['approved', 'active', 'disbursed'].includes(loan.loanStatus)
                  ).length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <Calendar className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Last Submission</p>
                <p className="text-sm font-bold text-gray-900">
                  {financialStatements.length > 0 
                    ? formatDate(financialStatements[0].created_at)
                    : 'N/A'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Overview */}
        {loanData?.loanApplications?.length > 0 && (
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  Loan Portfolio
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Loan Amount:</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(
                        loanData.loanApplications.reduce((sum, loan) => 
                          sum + (loan.loanAmountApproved || 0), 0
                        )
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Active Loans:</span>
                    <span className="font-medium text-success-600">
                      {loanData.loanApplications.filter(loan => 
                        ['approved', 'active', 'disbursed'].includes(loan.loanStatus)
                      ).length} of {loanData.totalApplications}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Latest Application:</span>
                    <span className="font-medium">
                      {loanData.loanApplications.length > 0 
                        ? formatDate(loanData.loanApplications[0].applicationDate)
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Financial Health
                </h3>
              </div>
              <div className="card-body">
                {financialStatements.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Statements Submitted:</span>
                      <span className="font-bold text-success-600">
                        {financialStatements.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Latest P&L:</span>
                      <span className="font-medium">
                        {formatDate(
                          financialStatements.find(s => s.statement_type === 'profit_loss')?.created_at
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Latest Balance Sheet:</span>
                      <span className="font-medium">
                        {formatDate(
                          financialStatements.find(s => s.statement_type === 'balance_sheet')?.created_at
                        )}
                      </span>
                    </div>
                    <div className="mt-4 p-3 bg-success-50 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-success-600 mr-2" />
                        <span className="text-success-800 font-medium">
                          Financial data up to date
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <PieChart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No financial data yet</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Submit your financial statements to see insights
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Financial Data Visualizations */}
        {financialStatements.length > 0 && (
          <FinancialDataCharts 
            financialStatements={financialStatements} 
            formatCurrency={formatCurrency} 
          />
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              </div>
              <div className="card-body space-y-4">
                <Link
                  to="/customer/financial-data"
                  className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <FileText className="h-8 w-8 text-primary-600" />
                    <div className="ml-4">
                      <h4 className="font-medium text-gray-900">Submit Financial Data</h4>
                      <p className="text-sm text-gray-600">Upload P&L and Balance Sheet</p>
                    </div>
                  </div>
                </Link>

                <Link 
                  to="/customer/loans"
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center">
                    <Building className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <h4 className="font-medium text-gray-900">View Loan Details</h4>
                      <p className="text-sm text-gray-600">Manage your loans and payments</p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Recent Financial Statements</h3>
              </div>
              <div className="card-body">
                {financialStatements.length > 0 ? (
                  <div className="space-y-4">
                    {financialStatements.slice(0, 5).map((statement, index) => (
                      <div key={statement.id || index} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                        <div className="p-2 rounded-full bg-success-100">
                          {statement.statement_type === 'profit_loss' ? (
                            <BarChart3 className="h-5 w-5 text-success-600" />
                          ) : (
                            <PieChart className="h-5 w-5 text-success-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {statement.statement_type === 'profit_loss' 
                              ? 'Profit & Loss Statement' 
                              : 'Balance Sheet'
                            }
                          </p>
                          <p className="text-sm text-gray-600">
                            Reporting Period: {formatDate(statement.reporting_period)}
                          </p>
                          <p className="text-sm text-gray-500">
                            Submitted: {formatDate(statement.created_at)}
                          </p>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 bg-success-100 text-success-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Submitted
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No financial statements submitted yet</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Start by submitting your financial data
                    </p>
                    <Link
                      to="/customer/financial-data"
                      className="btn btn-primary btn-sm mt-4"
                    >
                      Submit Now
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Loans */}
        <div className="mt-8">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Recent Loan Applications
              </h3>
            </div>
            <div className="card-body">
              {loanData?.loanApplications?.length > 0 ? (
                <>
                  <div className="grid gap-4">
                    {loanData.loanApplications.slice(0, 5).map((loan, index) => (
                      <div key={loan.applicationId || index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="p-2 rounded-full bg-primary-100">
                            <DollarSign className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {loan.loanProductName || 'Loan Application'}
                            </p>
                            <p className="text-sm text-gray-600">
                              Applied: {formatDate(loan.applicationDate)}
                            </p>
                            <p className="text-sm text-gray-500">
                              Purpose: {loan.loanPurpose || 'Not specified'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">
                            {formatCurrency(loan.loanAmountApproved || loan.loanAmountApplied)}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            ['approved', 'active', 'disbursed'].includes(loan.loanStatus)
                              ? 'bg-success-100 text-success-800'
                              : loan.loanStatus === 'pending'
                              ? 'bg-warning-100 text-warning-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {loan.loanStatus?.charAt(0).toUpperCase() + loan.loanStatus?.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* View All Loans Button */}
                  {loanData.loanApplications.length > 5 && (
                    <div className="mt-4 text-center">
                      <p className="text-sm text-gray-500 mb-2">
                        Showing 5 of {loanData.totalApplications} loan applications
                      </p>
                      <Link to="/customer/loans" className="btn btn-secondary btn-sm">
                        View All Loans
                      </Link>
                    </div>
                  )}
                  
                  {/* Always show View All button if there are any loans */}
                  {loanData.loanApplications.length > 0 && loanData.loanApplications.length <= 5 && (
                    <div className="mt-4 text-center">
                      <Link to="/customer/loans" className="btn btn-secondary btn-sm">
                        View Loan Details
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No loan applications yet</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Contact your MG administrator for loan application information
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-primary-50 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="p-2 bg-primary-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-gray-600 mb-4">
                If you have any questions about submitting your financial data or using the platform, 
                please contact your MG administrator or our support team.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerDashboard
